import * as React from 'react';
import {
  DispatchToPropsForMaterial,
  StateToPropsForMaterial,
} from '../Types/MaterialContainer';
import MaterialNavigation from './MaterialNavigation';
import { Link, RouteComponentProps } from 'react-router-dom';
import {
  isAttachment,
  isMaterialWithHtmlContent,
  isPresentation,
  isQuiz,
} from '../../CourseInstance/Utils/MaterialsHelper';

import { WithTranslation, withTranslation } from 'react-i18next';
import {
  ActionsSection,
  MaterialBox,
  MaterialHeader,
  MaterialNavigationSection,
  MaterialPage,
  MaterialsContainer,
  ReadButton,
  TagsContainer,
} from '../Styles/Materials';
import { CourseTopBar } from '../../Course/Styles/Course';
import { backArrow } from '../../../Assets/images/backArrow';
import Links from '../../Course/Components/Links';
import HtmlMaterial from './HtmlMaterial';
import Quiz from './Quiz';
import Presentation from './Presentation';
import Attachment from './Attachment';
import { Tag } from '../../../__core/Styles/Tag/Tag';
import OptionsBar from './OptionsBar';
import { PrimaryModernButton } from '../../../__core/Styles/Buttons/PrimaryModernButton';
import { SecondaryModernButton } from '../../../__core/Styles/Buttons/SecondaryModernButton';
import { checkIcon, githubIcon } from '../../../Assets/images/Icons';
import LoaderProvider from '../../../__core/Providers/Loader/LoaderProvider';
import { IMaterial } from '../../../__core/Interfaces/IMaterial';
import COURSE_ROLES from '../../../__core/Enums/CourseRoles';
import { havePermissions } from '../../../__core/Providers/Permissions/Permissions';
import MaterialFileNameBuilder from '../../CourseInstance/Utils/MaterialFileNameBuilder';
import MATERIAL_TYPES from '../../../__core/Enums/MaterialTypes';
import PASS_STATUS from '../../../__core/Enums/PassStatus';
import MediaQuery from 'react-responsive';
import { defaultTheme } from '../../../__core/Themes/Default';
import { BackToChapterButton } from '../Styles/MaterialNavigation';
import MATERIAL_ACCESS from '../../../__core/Enums/MaterialAccess';
import ProgressIcon from '../../../__core/Components/ProgressStatuses/ProgressIcon';
import SearchResults from '../../Course/Components/SearchResults';
import { SEARCH_PARAM_NAME } from '../../Course/Utils/Constraints';
import AppConfig from '../../../Configs/AppConfig';
import { TransparentButton } from '../../../__core/Styles/Buttons/TransparentButton';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSync } from '@fortawesome/free-solid-svg-icons';
import { get } from 'lodash';

type MaterialProps = DispatchToPropsForMaterial &
  StateToPropsForMaterial &
  RouteComponentProps &
  WithTranslation;

type MaterialState = {
  sidebarCollapse: boolean;
  abortController: AbortController;
  showRefreshButton: boolean;
};

class Material extends React.PureComponent<MaterialProps, MaterialState> {
  state = {
    sidebarCollapse: false,
    abortController: new AbortController(),
    showRefreshButton: true,
  };

  materialContainerRef = React.createRef<HTMLDivElement>();
  showRefreshButtonTimerAfterOneSecond: NodeJS.Timeout | null = null;

  componentDidMount(): void {
    const { materialId } = this.props;

    if (materialId) {
      this.getMaterial();

      if (this.isMobile()) {
        this.setState({ sidebarCollapse: true });
      }
    }

    this.getSideMenu();
    this.getCourseInstance();
  }

  componentWillUnmount(): void {
    const {
      actions: { clearMaterialData },
    } = this.props;

    clearMaterialData();
    if (this.showRefreshButtonTimerAfterOneSecond) {
      clearTimeout(this.showRefreshButtonTimerAfterOneSecond);
    }
  }

  isMobile = (): boolean =>
    window.matchMedia(`(${defaultTheme.MEDIA_MAX.tablet})`).matches;

  getMaterial = async (): Promise<void> => {
    const {
      actions: { fetchMaterial },
      materialId,
      slug,
      instanceId,
      history,
    } = this.props;

    try {
      const response = await fetchMaterial(instanceId, slug, materialId);
      const presentationType = get(response, 'data.type');
      if (presentationType === MATERIAL_TYPES.PRESENTATION_PDF) {
        this.fetchPdfBase64Data();
      }
    } catch (e) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      if (e.status === 303) {
        const {
          metadata: { slug },
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
        } = await e.json();
        history.replace(`/material/${instanceId}/${slug}/${materialId}`);
      } else {
        history.push('/404');
      }
    }
  };

  getMaterialResult = (): void => {
    const {
      actions: { fetchMaterial },
      materialId,
      slug,
      instanceId,
    } = this.props;

    fetchMaterial(instanceId, slug, materialId, false);
    this.setState({ showRefreshButton: false });
    this.showRefreshButtonTimerAfterOneSecond = setTimeout(
      () => this.setState({ showRefreshButton: true }),
      60000
    );
  };

  setArticleReadStatus = async (): Promise<void> => {
    const {
      actions: { setArticleRead, fetchMaterial, fetchSideMenu },
      instanceId,
      materialId,
      slug,
      history,
      material,
    } = this.props;

    const { abortController } = this.state;

    const newStatus = Number(material.status === PASS_STATUS.DONE ? 0 : 1);

    try {
      await setArticleRead(instanceId, materialId, newStatus);
      fetchMaterial(instanceId, slug, materialId, false);
      fetchSideMenu(instanceId, slug, abortController.signal);
    } catch (e) {
      history.push('/404');
    }
  };

  getSideMenu = async (): Promise<void> => {
    const {
      actions: { fetchSideMenu },
      slug,
      instanceId,
      history,
    } = this.props;

    const { abortController } = this.state;

    try {
      await fetchSideMenu(instanceId, slug, abortController.signal);
    } catch (e) {
      history.push('/404');
    }
  };

  getCourseInstance = async (): Promise<void> => {
    const {
      actions: { fetchCourseInstance },
      history,
      instanceId,
      courseInstance,
    } = this.props;

    if (instanceId !== courseInstance.id) {
      try {
        await fetchCourseInstance({ id: instanceId });
      } catch (e) {
        history.push('/404');
      }
    }
  };

  downloadMaterial = (): void => {
    const {
      actions: { downloadMaterial },
      material,
      slug,
      instanceId,
    } = this.props;

    const materialFileNameBuilder = new MaterialFileNameBuilder(material);

    downloadMaterial(
      material.id,
      slug,
      instanceId,
      materialFileNameBuilder.getFileNameName()
    );
  };

  fetchPdfBase64Data = (): void => {
    const {
      actions: { fetchPdfBase64 },
      material,
      slug,
      instanceId,
    } = this.props;

    // TODO: add loader
    fetchPdfBase64(instanceId, slug, material.id);
  };

  addReview = async (
    justification: string | null,
    grade: number
  ): Promise<void> => {
    const {
      history,
      materialId,
      instanceId,
      slug,
      actions: { setMaterialReview, fetchMaterial },
    } = this.props;

    try {
      await setMaterialReview(materialId, instanceId, justification, grade);
      fetchMaterial(instanceId, slug, materialId);
    } catch (e) {
      history.push('/404');
    }
  };

  addIssue = async (message: string, selected: string): Promise<void> => {
    const {
      history,
      materialId,
      material,
      instanceId,
      actions: { setMaterialIssue },
      location: { search },
    } = this.props;

    let messageWithParams = message;

    if (material.type === MATERIAL_TYPES.PRESENTATION_PDF) {
      const params = new URLSearchParams(search);
      const slideNumber = params.get('page');
      messageWithParams = `Slide number: ${slideNumber} - ${message}`;
    }

    try {
      await setMaterialIssue(
        materialId,
        instanceId,
        messageWithParams,
        selected
      );
    } catch (e) {
      history.push('/404');
    }
  };

  hasSearchParam = (): boolean => {
    const { location } = this.props;
    const params = new URLSearchParams(location.search);
    return !!params.get(SEARCH_PARAM_NAME);
  };

  componentDidUpdate(prevProps: Readonly<MaterialProps>): void {
    const {
      materialId: currentMaterialId,
      slug: currentSlug,
      menu: currentMenu,
      history,
      instanceId,
      location,
      material,
    } = this.props;
    const {
      materialId: prevMaterialId,
      slug: prevSlug,
      location: prevLocation,
    } = prevProps;

    if (
      currentMaterialId &&
      currentSlug &&
      (currentMaterialId !== prevMaterialId || currentSlug !== prevSlug)
    ) {
      this.getDataAfterMaterialChange();
    }

    if (!currentMaterialId && !currentMaterialId && !!currentMenu.id) {
      const chapter = currentMenu.chapters.find(
        (chapter) => `${currentMenu.number}-${chapter.number}` === currentSlug
      );
      if (chapter) {
        const [firstMaterial] = chapter.materials;
        const { id: materialId = '' } = firstMaterial;

        if (materialId) {
          history.replace(
            `/material/${instanceId}/${currentSlug}/${materialId}`
          );
        }
      }
    }

    if (location.pathname !== prevLocation.pathname) {
      const element = document.getElementById('material-container');

      if (element)
        element.scrollTo({
          top: 0,
          behavior: 'smooth',
        });
    }
  }

  getDataAfterMaterialChange = async (): Promise<void> => {
    const { abortController } = this.state;
    abortController.abort();

    this.setState({ abortController: new AbortController() }, async () => {
      await this.getMaterial();
      this.getSideMenu();
    });
  };

  navigateToMaterial = (slug: string, materialId: string): void => {
    const {
      history: { push },
      instanceId,
    } = this.props;

    const materialUrl = `/material/${instanceId}/${slug}/${materialId}`;
    push(materialUrl);
  };

  getNextMaterialButton = (): React.ReactElement | undefined => {
    const { t, navigation } = this.props;

    return (
      navigation && (
        <PrimaryModernButton
          disabled={!navigation.nextSlug || !navigation.nextMaterialId}
          onClick={() =>
            !!navigation.nextSlug &&
            !!navigation.nextMaterialId &&
            this.navigateToMaterial(
              navigation.nextSlug,
              navigation.nextMaterialId
            )
          }
        >
          {t('Next')}
        </PrimaryModernButton>
      )
    );
  };

  getPreviousMaterialButton = (): React.ReactElement | undefined => {
    const { t, navigation } = this.props;

    return (
      navigation && (
        <SecondaryModernButton
          disabled={!navigation.previousSlug || !navigation.previousMaterialId}
          onClick={() =>
            !!navigation.previousSlug &&
            !!navigation.previousMaterialId &&
            this.navigateToMaterial(
              navigation.previousSlug,
              navigation.previousMaterialId
            )
          }
        >
          {t('Previous')}
        </SecondaryModernButton>
      )
    );
  };

  toggleSidebar = (): void =>
    this.setState((state) => ({ sidebarCollapse: !state.sidebarCollapse }));

  renderMaterial = (): React.ReactNode => {
    const { material, t, currentMaterialBase64 } = this.props;

    return (
      <MaterialBox ref={this.materialContainerRef}>
        {isMaterialWithHtmlContent(material.type) && (
          <HtmlMaterial material={material} />
        )}
        {isQuiz(material.type) && (
          <Quiz surveyUrl={material.content.externalUrl} />
        )}
        {isPresentation(material.type) && (
          <Presentation
            material={material}
            downloadMaterial={this.downloadMaterial}
            hasSearchParam={this.hasSearchParam()}
            base64={currentMaterialBase64}
          />
        )}
        {isAttachment(material.type) && (
          <Attachment downloadMaterial={this.downloadMaterial} />
        )}
        {material.content.githubUrl && (
          <ActionsSection>
            <SecondaryModernButton
              as="a"
              href={material.content.githubUrl}
              target="_blank"
            >
              {githubIcon} &nbsp; {t('Go to repository')}
            </SecondaryModernButton>
          </ActionsSection>
        )}

        {material.type !== MATERIAL_TYPES.QUIZ && material.progress && (
          <ActionsSection>
            <ReadButton
              className="material-read-button"
              active={material.status === PASS_STATUS.DONE}
              onClick={this.setArticleReadStatus}
            >
              {t(material.type === MATERIAL_TYPES.EXERCISE ? 'Done' : 'Read')}
              {material.status === PASS_STATUS.NOT_VISITED ||
                (material.status !== PASS_STATUS.VISITED && checkIcon)}
            </ReadButton>
          </ActionsSection>
        )}
      </MaterialBox>
    );
  };

  getMaterialScore = (material: IMaterial): React.ReactNode => {
    const { t } = this.props;
    const { showRefreshButton } = this.state;

    return material.type === MATERIAL_TYPES.QUIZ ? (
      <div className="quiz-status">
        {showRefreshButton && (
          <TransparentButton
            onClick={this.getMaterialResult}
            title={t('Refresh quiz result. Available once every minute.')}
          >
            <FontAwesomeIcon icon={faSync} />
          </TransparentButton>
        )}
        <span>
          {`${material.result?.score}/${material.result?.maxScore}`}&nbsp;
          {t('pts.')}
        </span>
        {material.progress && (
          <ProgressIcon
            points={material.progress.done}
            maxPoints={material.progress.all}
          />
        )}
      </div>
    ) : (
      <div className="status-icon" onClick={this.setArticleReadStatus}>
        {material.progress && (
          <ProgressIcon
            points={material.progress.done}
            maxPoints={material.progress.all}
          />
        )}
      </div>
    );
  };

  goToChapterInMobile = (): void => {
    const { menu, materialId } = this.props;

    this.toggleSidebar();

    menu.chapters.forEach((chapter) => {
      const material = chapter.materials.find(
        (material) => material.id === materialId
      );

      if (material) {
        const element = document.getElementById(`chapter-mobile-${chapter.id}`);

        if (element) {
          element.scrollIntoView();
        }
      }
    });
  };

  getChapterNameByMaterialId = (materialId: string): string => {
    const { menu } = this.props;
    let chapterName = '';
    menu.chapters.forEach((chapter) => {
      const material = chapter.materials.find(
        (material) => material.id === materialId
      );

      if (material) {
        chapterName = chapter.name;
      }
    });
    return chapterName;
  };

  render(): React.ReactElement {
    const {
      courseInstance: {
        product: { name: productName = '' },
        signature,
        links,
      },
      material,
      fetchMaterialInProgress,
      userCourseRoles,
      menu,
      slug,
      instanceId,
    } = this.props;

    const { sidebarCollapse } = this.state;
    const courseLink = (
      <Link className="backLink" to={`/course/${signature}`}>
        {backArrow}
        {productName}
      </Link>
    );

    return (
      <>
        <CourseTopBar>
          <div className="left-side-links">
            <MediaQuery minDeviceWidth={defaultTheme.SCREEN_SIZES.tablet}>
              {(matches) =>
                matches ? (
                  courseLink
                ) : sidebarCollapse ? (
                  <BackToChapterButton onClick={this.goToChapterInMobile}>
                    {backArrow} {this.getChapterNameByMaterialId(material.id)}
                  </BackToChapterButton>
                ) : (
                  courseLink
                )
              }
            </MediaQuery>
            <div id="clear-search-portal" />
          </div>
          <Links links={links} />
        </CourseTopBar>

        {instanceId && <SearchResults instanceId={instanceId} />}

        <MaterialPage>
          <div>
            <MaterialNavigation
              toggleSidebar={this.toggleSidebar}
              sidebarCollapse={sidebarCollapse}
              menuItems={menu}
              slug={slug}
              instanceId={instanceId}
              userRoles={userCourseRoles}
            />
          </div>
          <MaterialsContainer
            id="material-container"
            sidebarCollapse={sidebarCollapse}
          >
            <LoaderProvider isLoading={fetchMaterialInProgress}>
              <MaterialHeader>
                <h1>{material.name}</h1>
                {this.getMaterialScore(material)}
              </MaterialHeader>
              <TagsContainer>
                {material.tags.map((tag) => (
                  <Tag as="span" key={tag}>
                    {tag}
                  </Tag>
                ))}
              </TagsContainer>
              {this.renderMaterial()}
              {havePermissions(userCourseRoles, [
                COURSE_ROLES.ROLE_USER,
                COURSE_ROLES.ROLE_LECTURER,
                COURSE_ROLES.ROLE_MENTOR,
              ]) && (
                <OptionsBar
                  materialContainerRef={this.materialContainerRef}
                  addReview={this.addReview}
                  addIssue={this.addIssue}
                  review={material.review}
                  showIssueSection={
                    material.type !== MATERIAL_TYPES.PRESENTATION_REVEAL &&
                    !!(AppConfig.FEATURE_MATERIAL_ISSUE ?? 1)
                  }
                  showStarSection={material.access !== MATERIAL_ACCESS.LECTURER}
                />
              )}
              <MaterialNavigationSection>
                {this.getPreviousMaterialButton()}
                {this.getNextMaterialButton()}
              </MaterialNavigationSection>
            </LoaderProvider>
          </MaterialsContainer>
        </MaterialPage>
      </>
    );
  }
}

export default withTranslation()(Material);
